/**
 * Every word the page shows, in one place. Nothing in the logic holds a sentence of its own.
 *
 * Two conventions run through the copy. Directions are named from the visitor's side — "fra jer
 * til os", never "inbound" — because the wire names invert depending on who is speaking. And
 * anything unmeasured says so: "ikke målt" is never softened into a pass.
 */

import { PLATFORM_SUBNET } from '@/config/targets'
import { BRAND } from '@/lib/brand'
import type { FailureKind } from '@/lib/calltest/api-failure'
import type { CallFailure, CallPhase } from '@/lib/calltest/sip'
import type { FindingCode, QualityReason, TerminatedReason, Verdict } from '@/lib/calltest/types'
import type { OverallStatus, TcpStatus, UdpStatus } from '@/lib/status'

export const page = {
	logo: BRAND,
	title: 'Forbindelsestjek',
	intro: `Testen måler fra denne browser, om jeres netværk kan bruge IP-telefoni hos ${BRAND} — både om firewallen lukker igennem, og hvordan et rigtigt opkald lyder. Kør den fra det netværk, telefonerne skal stå på.`,
	introPoints: [
		'Testen beder ikke om adgang til mikrofon eller kamera.',
		'Der bliver ringet til en testbesked, som varer omkring 20 sekunder.',
		'Browseren sender selv en tone tilbage, så lyden kan måles begge veje.',
		'Det hele tager omkring 45 sekunder, og I skal ikke foretage jer noget.'
	],
	start: 'Start test',
	rerun: 'Kør testen igen',
	running: 'Tester …',
	stop: 'Stop testen',
	footnote: `Testen sender ingen persondata og bruger hverken mikrofon eller kamera. Den måler netværksforbindelse og lydkvalitet fra denne browser til ${BRAND}, og lyden den sender er en tone, browseren selv laver. Et grønt resultat betyder, at det testen kan måle er i orden — de manuelle punkter skal stadig bekræftes.`,
	tapForSound: 'Tryk for at høre lyden',
	audioLabel: `Testbesked fra ${BRAND}`
} as const

export const banner: Record<OverallStatus, { headline: string, body: string }> = {
	run: {
		headline: 'Tester forbindelsen …',
		body: 'Bliv på siden, mens testen kører. I skal ikke foretage jer noget undervejs.'
	},
	ok: {
		headline: 'Alt ser klar ud til IP-telefoni',
		body: `Browseren nåede ${BRAND}, og testopkaldet gik rent igennem begge veje. Gennemgå den manuelle tjekliste, før I sætter telefonerne op.`
	},
	warn: {
		headline: 'Det virker, men der er en advarsel',
		body: 'Læs advarslerne nedenfor. Telefonerne kan registrere og ringe, men noget bør rettes først.'
	},
	fail: {
		headline: 'Der er problemer med forbindelsen',
		body: 'Send firewall-kravene nedenfor til jeres IT, og kør testen igen når reglerne er på plads.'
	}
}

export const phase: Record<CallPhase | 'firewall' | 'provisioning' | 'analysing', string> = {
	firewall: 'Tjekker firewall …',
	provisioning: 'Henter testadgang …',
	registering: 'Registrerer telefonen …',
	dialling: 'Ringer op …',
	ringing: 'Det ringer …',
	'in-call': 'Opkaldet er i gang — måler lyden begge veje',
	ended: 'Opkaldet er slut',
	analysing: 'Henter platformens måling …'
} as const

export const firewall = {
	tcpRow: (label: string) => `TCP 443 → ${label}`,
	udpRow: 'UDP 3478 → NAT-test (STUN)',
	publicIp: 'Jeres offentlige IP',
	ipPending: 'bestemmes …',
	ipUnknown: 'kunne ikke bestemmes'
} as const

export const tcpLabel: Record<TcpStatus, string> = {
	pending: 'Tester…',
	ok: 'OK',
	blocked: 'Blokeret'
}

export const udpLabel: Record<UdpStatus, string> = {
	pending: 'Tester…',
	ok: 'OK',
	symmetric: 'Advarsel',
	'subnet-blocked': 'Blokeret',
	'udp-blocked': 'Blokeret'
}

export const udpDetail: Record<UdpStatus, string> = {
	pending: 'Spørger vores STUN-servere, hvilken adresse de ser jer fra.',
	ok: `STUN svarede fra ${BRAND}. NAT'en holder samme port mod alle destinationer, hvilket er det telefonien har brug for.`,
	symmetric: `STUN svarede fra ${BRAND}, men NAT'en tildeler en ny port pr. destination (symmetrisk NAT). Det kan give envejs-lyd eller helt manglende lyd.`,
	'subnet-blocked': `UDP kommer ud på internettet, men intet svar kom fra ${PLATFORM_SUBNET} på port 3478. Firewallen blokerer netop det subnet.`,
	'udp-blocked': 'Ingen STUN-servere svarede — hverken vores egne eller offentlige. UDP ser blokeret ud herfra.'
}

export const call = {
	sendingTone: 'Sender testtone',
	// Named as a test tone on purpose: this direction proves the network path, not that anyone's
	// microphone works, and this label is the one place that confusion would arise.
	yourAudio: 'Testtone fra jer til os',
	ourAudio: 'Lyd fra os til jer',
	heardSeconds: (audible: number, total: number) => `Hørt lyd i ${audible} af ${total} sekunder`,
	toneExplainer: 'Retningen fra jer til os måles med en tone, browseren selv laver — ikke med en mikrofon. Det tester netværksstien og lydkvaliteten, men siger intet om jeres headset eller telefon.',
	deliveredButSilent: 'Lydpakkerne kom frem, men der kom ingen hørbar lyd ud af dem. Det peger på codec eller gateway, ikke på netværket.',
	notMeasured: 'ikke målt',
	notReported: 'ikke rapporteret'
} as const

/** The one sentence the result screen leads with. */
export const verdictHeadline: Record<Verdict, string> = {
	OK: 'Opkald virker godt fra dette netværk.',
	DEGRADED: 'Opkald virker, men lyden kan være ujævn.',
	BAD: 'Der er et problem med lyden.',
	NO_AUDIO: 'Der er et problem — lyden gik kun den ene vej.',
	NOT_MEASURED: 'Opkaldet blev forbundet, men der var ikke lyd nok til at bedømme det.'
}

export const direction = {
	INBOUND: 'Det er retningen fra jer til os, der er værst.',
	OUTBOUND: 'Det er retningen fra os til jer, der er værst.'
} as const

export const oneWay = {
	INBOUND: 'Der nåede ingen lyd fra jer frem til os.',
	OUTBOUND: 'Der nåede ingen lyd fra os frem til jer.'
} as const

export const notConnected = (reason: TerminatedReason) =>
	`Opkaldet blev aldrig forbundet — ${terminatedReason[reason]}.`

export const terminatedReason: Record<TerminatedReason, string> = {
	CANCEL: 'det blev afbrudt, før det blev besvaret',
	HANGUP: 'der blev lagt på',
	BUSY: 'linjen var optaget',
	UNREACHABLE: 'det kunne ikke rutes',
	ERROR: 'der opstod en fejl',
	BLOCKED: 'platformen afviste opkaldet',
	NOT_FOUND: 'nummeret blev ikke fundet'
}

/** Wraps a reason fragment into a sentence, so the fragments stay usable in both positions. */
export const qualityReasonNote = (reason: QualityReason) =>
	`Platformen kunne ikke bedømme opkaldet: ${qualityReason[reason]}.`

export const qualityReason: Record<QualityReason, string> = {
	NO_RTCP_FROM_FAR_END: 'browseren sendte ingen brugbare kvalitetsrapporter, så retningen fra os til jer kan ikke bedømmes',
	LEG_TOO_SHORT: 'opkaldet var for kort til at bedømme',
	NO_MEDIA_TIME: 'der blev ikke målt nogen medietid',
	PRE_ROLLOUT_CDR: 'opkaldet blev registreret, før målingen blev slået til',
	CALL_NOT_ANSWERED: 'opkaldet blev ikke besvaret',
	NO_RTP_LEGS: 'der var ingen lydstrømme at måle'
}

export const finding: Record<FindingCode, string> = {
	NO_INBOUND_AUDIO: 'Vi modtog ingen lyd fra jer.',
	INBOUND_DELIVERY_LOW: 'Der kom færre lydpakker fra jer, end der skulle.',
	INBOUND_LOSS_HIGH: 'For stort pakketab på lyden fra jer til os.',
	INBOUND_JITTER_HIGH: 'Lyden fra jer til os kom ujævnt frem.',
	OUTBOUND_LOSS_HIGH: 'For stort pakketab på lyden fra os til jer.',
	OUTBOUND_JITTER_HIGH: 'Lyden fra os til jer kom ujævnt frem.',
	RTT_HIGH: 'Svartiden til platformen var for høj.',
	ALL_LEGS_INBOUND_BAD: 'Lyden fra jer var dårlig gennem hele opkaldet.',
	SINGLE_LEG_INBOUND_BAD: 'Lyden fra jer var dårlig i en del af opkaldet.'
}

export const apiFailure: Record<FailureKind, string> = {
	busy: 'Testlinjen er optaget lige nu — der kører allerede det maksimale antal test. Prøv igen om et minut.',
	disabled: 'Opkaldstesten er midlertidigt utilgængelig. Det er noget hos os, ikke hos jer — jeres netværk er ikke årsagen.',
	'rate-limited': 'Der er sendt for mange forespørgsler fra denne adresse. Vent et øjeblik, og prøv igen.',
	'bad-session-id': 'Der opstod en intern fejl i testen. Prøv igen.',
	'session-gone': 'Testadgangen er udløbet. Kør testen igen.',
	server: 'Vi kunne ikke starte opkaldstesten. Prøv igen om lidt.',
	network: 'Vi kunne ikke få forbindelse til vores API. Det kan være firewallen — se firewall-tjekket ovenfor.'
}

export const callFailure: Record<CallFailure, string> = {
	'wss-blocked': 'Jeres netværk blokerer forbindelsen til vores telefoni-server (WebSocket over port 443). Det er ikke opkaldet, der fejlede — forbindelsen blev aldrig etableret.',
	'registration-failed': 'Telefonen kunne ikke registrere sig på platformen.',
	'call-rejected': 'Opkaldet blev afvist af platformen.',
	'no-answer': 'Opkaldet blev ikke besvaret inden for tidsgrænsen.',
	'media-failed': 'Lyden kunne ikke sættes op i browseren.'
}

export const pendingGaveUp = 'Platformen har endnu ikke gjort sin måling færdig. Nedenfor står det, browseren selv målte.'

export const noPlatformAudio = {
	notAnswered: 'Opkaldet blev ikke besvaret, så der er ingen måling fra platformen.',
	countersMissing: 'Opkaldet blev gennemført, men platformens egne tællere nåede ikke frem. Kun browserens egne målinger findes.'
} as const

export const comparison = {
	heading: 'To uafhængige målinger',
	lede: 'Platformen og browseren har målt det samme opkald hver for sig. Er de enige, er tallene til at stole på — er de uenige, er uenigheden i sig selv et fingerpeg.',
	platform: 'Målt af platformen',
	browser: 'Målt af denne browser',
	metric: 'Måling',
	windowExplainer: 'Platformen læser sine tællere ved opkaldets slutning på omkring halvdelen af alle opkald; ellers er de den nyeste måling taget undervejs. Dens måleperiode kan derfor være kortere end opkaldet, og totalerne tilsvarende lavere, uden at der er noget galt. Sammenlign pakker pr. sekund — det tal afhænger ikke af, hvor længe hver side har målt.',
	rows: {
		packets: 'Pakker',
		window: 'Måleperiode',
		loss: 'Pakketab',
		jitter: 'Jitter',
		rtt: 'Svartid',
		level: 'Niveau på testtone'
	}
} as const

export const technical = {
	summary: 'Tekniske detaljer',
	lede: 'Support beder om disse tal. Testadgangens legitimationsoplysninger er ikke med.',
	copy: 'Kopiér detaljer',
	copied: 'Kopieret',
	copyFailed: 'Browseren tillod ikke kopiering. Markér teksten herunder i stedet:'
} as const

export const caveat = {
	heading: 'Grønt er ikke det samme som 100 %',
	body: 'Grønt betyder, at det testen kan måle er i orden. Det er ikke en garanti. En browser kan ikke teste SIP ALG, fragmenterede UDP-pakker, hele tale-portområdet 5000-65535 eller NTP.',
	last: 'Gennemgå derfor også den manuelle tjekliste. Den endelige prøve er en telefon, der registrerer og kan ringe.'
} as const

export const checklist = {
	heading: 'Manuel tjekliste',
	lede: 'Disse punkter kan ikke måles fra en browser — bekræft dem hos jeres IT.',
	copy: 'Kopiér reglerne til jeres IT',
	copied: 'Kopieret',
	copyFailed: 'Browseren tillod ikke kopiering. Markér teksten herunder i stedet:',
	items: [
		{ text: 'SIP ALG / SIP inspection / SIP Helper skal være slået FRA.', note: null },
		{ text: 'UDP connection timeout skal være over 65 sekunder.', note: null },
		{
			text: `Fuld port-range til ${PLATFORM_SUBNET}: UDP og TCP på port 80, 443, 3478 og 5000-65535.`,
			note: '443 og 3478 tjekkes automatisk ovenfor. Selve tale-rangen kan ikke måles fra en browser.'
		},
		{ text: 'Fragmenterede UDP-pakker må ikke droppes.', note: null },
		{
			text: 'UDP port 123 (NTP) til alle IP-adresser.',
			note: 'Kun relevant ved brug af bordtelefoner.'
		}
	]
} as const

export const progress = {
	completed: (done: number, total: number) => `${done} af ${total} trin gennemført`,
	remaining: (seconds: number) => seconds > 0 ? `ca. ${seconds} sek. tilbage` : 'færdiggør de sidste trin'
} as const
